/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps, CSSProperties } from 'react'
import { forwardRef } from 'react'

import { cn } from '@/lib/utils'

export interface HotSpotIndicatorProps {
  /** Props applied to the floating wrapper (typically absolute positioning). */
  floatProps?: ComponentProps<'div'>
  /** Props applied to the inner indicator shell. */
  boxProps?: ComponentProps<'div'>
  /** CSS color for the dot and pulse rings (e.g. `oklch(...)`, `#e53e3e`). Uses theme primary when omitted. */
  color?: string
}

export const HotSpotIndicator = forwardRef<HTMLDivElement, HotSpotIndicatorProps>((props, forwardedRef) => {
  const { floatProps, boxProps, color } = props
  const { className: floatClassName, style: floatStyle, ...restFloat } = floatProps ?? {}
  const { className: boxClassName, ...restBox } = boxProps ?? {}

  const tinted = Boolean(color)
  const tintStyle = tinted ? ({ ['--sf-hot-spot-color' as string]: color } as CSSProperties) : undefined

  return (
    <div
      className={cn('pointer-events-none absolute z-10', floatClassName)}
      ref={forwardedRef}
      style={{ ...floatStyle, ...tintStyle }}
      {...restFloat}
    >
      <div
        className={cn(
          'relative inline-flex items-center justify-center',
          !tinted && '[--sf-hot-spot-color:var(--primary)]',
          boxClassName
        )}
        {...restBox}
      >
        <span
          className={cn('absolute z-3 size-[5px] rounded-full', tinted ? '' : 'bg-[var(--sf-hot-spot-color)]')}
          style={tinted ? { backgroundColor: color } : undefined}
        />

        <span
          className={cn(
            'absolute z-2 size-3 rounded-full border-2 border-solid',
            tinted ? '' : 'border-[var(--sf-hot-spot-color)]'
          )}
          style={{
            animation: 'sf-hot-spot-pulse 2s ease-out infinite',
            borderColor: tinted ? color : undefined,
          }}
        />
        <span
          className={cn(
            'absolute z-1 size-3 rounded-full border border-solid',
            tinted ? '' : 'border-[var(--sf-hot-spot-color)]'
          )}
          style={{
            animation: 'sf-hot-spot-pulse 2s ease-out infinite 1s',
            borderColor: tinted ? color : undefined,
          }}
        />
      </div>
    </div>
  )
})

HotSpotIndicator.displayName = 'HotSpotIndicator'
